import * as React from 'react';
// Prefer local shadcn popover, fallback to Radix if not present
// eslint-disable-next-line
// @ts-ignore
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
// If your project doesn't have a popover.tsx in components/ui, install or swap to Radix directly:
// import * as PopoverPrimitive from '@radix-ui/react-popover';
// const { Root: Popover, Content: PopoverContent, Trigger: PopoverTrigger } = PopoverPrimitive;
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CalendarIcon, Clock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export interface DateTimePickerProps {
    value?: Date | null;
    onChange?: (date: Date | null) => void;
    required?: boolean;
}

// Combine a date-only and a HH:mm string into a Date
function combineDateAndTime(date: Date, time: string): Date {
    const [hStr, mStr] = time.split(':');
    const h = parseInt(hStr || '0', 10);
    const m = parseInt(mStr || '0', 10);
    const d = new Date(date);
    d.setHours(isNaN(h) ? 0 : h, isNaN(m) ? 0 : m, 0, 0);
    return d;
}

export function DateTimePicker({ value, onChange, required }: DateTimePickerProps) {
    // Default due time: 11:59 PM tomorrow
    function defaultDueDate(): Date {
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);
        tomorrow.setHours(23, 59, 0, 0);
        return tomorrow;
    }

    const [date, setDate] = React.useState<Date | undefined>(value ?? defaultDueDate());
    const [time, setTime] = React.useState<string>(() => {
        const base = value ?? defaultDueDate();
        const hh = String(base.getHours()).padStart(2, '0');
        const mm = String(base.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`; // defaults to 23:59
    });

    function isSameDay(a: Date, b: Date): boolean {
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    function getMinTimeForSelectedDate(selected?: Date): string | null {
        if (!selected) return null;
        // For dueAt, the minimum selectable date is strictly tomorrow at 23:59
        // If user picks tomorrow, min time is 23:59; if later than tomorrow, min time is 00:00
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const selectedDate = new Date(selected);
        selectedDate.setHours(0, 0, 0, 0);
        if (isSameDay(selectedDate, tomorrow)) return '23:59';
        if (selectedDate > tomorrow) return '00:00';
        // Selected date is before tomorrow → invalid (no valid times)
        return null;
    }

    React.useEffect(() => {
        if (!onChange) return;
        if (!date) { onChange(null); return; }
        const minTime = getMinTimeForSelectedDate(date);
        if (!time) {
            if (minTime === null) {
                onChange(null);
                return;
            }
            setTime(minTime);
            return;
        }
        if (minTime !== null && time < minTime) { setTime(minTime); return; }
        onChange(combineDateAndTime(date, time));
    }, [date, time]);

    return (
        <div className="flex items-center gap-2">
            <Popover>
                <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start w-[220px]">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? date.toLocaleDateString() : 'Pick a date'}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="z-50 bg-popover border rounded-md p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => setDate(d ?? defaultDueDate())}
                        disabled={(day) => {
                            const now = new Date();
                            const tomorrow = new Date(now);
                            tomorrow.setDate(now.getDate() + 1);
                            tomorrow.setHours(0, 0, 0, 0);
                            const candidate = new Date(day);
                            candidate.setHours(0, 0, 0, 0);
                            // Disable today and any past date; allow tomorrow and future days
                            return candidate < tomorrow;
                        }}
                        initialFocus
                    />
                </PopoverContent>
            </Popover>
            <div className="flex items-center gap-2">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-flex" tabIndex={0} aria-label="Scheduling rules">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent sideOffset={6}>
                        Due date must be at least tomorrow by 11:59 PM.
                    </TooltipContent>
                </Tooltip>
                <Input
                    type="time"
                    id="time-picker"
                    step="60"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    required={required}
                    min={getMinTimeForSelectedDate(date) || undefined}
                    disabled={date ? getMinTimeForSelectedDate(date) === null : false}
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none w-[120px]"
                />
            </div>
        </div>
    );
}

export function toIsoStringOrNull(d: Date | null): string | null {
    return d ? new Date(d).toISOString() : null;
}
